import {
  Box,
  Divider,
  Menu,
  MenuItem,
  Paper,
  type PaperProps,
  Stack,
  Typography,
  type TypographyProps,
  styled,
} from "@mui/material"
import { type ReactNode, isValidElement, useState } from "react"
import { Check, ChevronDown } from "react-feather"
import type { ReferentialCode } from "../../../types"
import { isDefined } from "../../../utils"
import {
  FILTER_FIELD_HEIGHT,
  filterFieldActiveBorderColor,
  filterFieldBorderColor,
} from "../FilterField/filterField.conf"

export interface DropdownSelectorProps<T extends string | number> {
  title?: ReactNode
  placeholder?: string
  value?: T
  hideValue?: boolean
  onChange: (value: T) => void
  options: ReferentialCode<T>[]
  slotProps?: {
    root?: PaperProps
    title?: TypographyProps
    value?: TypographyProps
  }
}

export const DropdownSelector = <T extends string | number>({
  title,
  placeholder,
  value,
  hideValue = false,
  onChange,
  options,
  slotProps = {},
}: DropdownSelectorProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const open = Boolean(anchorEl)

  const selectedLabel =
    options.find((option) => option.code === value)?.label ?? value

  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleSelect = (code: T) => {
    onChange(code)
    handleClose()
  }
  return (
    <>
      <DropdownContainer
        {...(slotProps?.root ?? {})}
        onClick={(e) => setAnchorEl((el) => (el ? null : e.currentTarget))}
      >
        <InnerContainer divider={<Divider flexItem orientation="vertical" />}>
          <Title title={title} slotProps={slotProps?.title} />
          <DropdownContent
            role="combobox"
            aria-haspopup="listbox"
            aria-label={typeof title === "string" ? title : "Sélecteur"}
            aria-expanded={open}
          >
            {!hideValue && (
              <Typography noWrap width="100%" {...(slotProps?.value ?? {})}>
                {selectedLabel ?? placeholder}
              </Typography>
            )}
            <ChevronDown
              style={{
                transition: "transform 0.2s",
                ...(open && { transform: "rotate(180deg)" }),
              }}
            />
          </DropdownContent>
        </InnerContainer>
      </DropdownContainer>
      <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {options.map((option) => (
          <StyledMenuItem
            key={option.code}
            onClick={() => handleSelect(option.code)}
            selected={value === option.code}
          >
            <Label component="span">{option.label}</Label>
            <CheckSlot
              color={slotProps?.value?.color}
              selected={value === option.code}
            >
              <Check size={16} />
            </CheckSlot>
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </>
  )
}

const Title = ({
  title,
  slotProps,
}: {
  title?: ReactNode
  slotProps?: TypographyProps
}) => {
  if (!isDefined(title)) return null
  return isValidElement(title) ? (
    title
  ) : (
    <DropdownTitle {...(slotProps ?? {})}>{title}</DropdownTitle>
  )
}

const DropdownContainer = styled(Paper)(({ theme: { palette } }) => ({
  display: "flex",
  borderRadius: "100px",
  minHeight: FILTER_FIELD_HEIGHT,
  width: "fit-content",
  borderColor: filterFieldBorderColor(palette),
  cursor: "pointer",
  "&:hover": {
    borderColor: filterFieldActiveBorderColor(palette),
  },
}))

const InnerContainer = styled(Stack)({
  flexDirection: "row",
}) as typeof Stack

const DropdownTitle = styled(Typography)(({ theme }) => ({
  display: "grid",
  placeContent: "center",
  fontWeight: 500,
  paddingInline: theme.spacing(1.75),
}))

const DropdownContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  paddingInline: theme.spacing(1.75),
}))

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    marginTop: theme.spacing(0.5),
    minWidth: 200,
    boxShadow: "1px 2px 6px 1px rgba(102, 108, 117, 0.5)",
    padding: `0 ${theme.spacing(1)}`,
  },
}))
StyledMenu.defaultProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "right" },
  transformOrigin: { vertical: "top", horizontal: "right" },
}

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  minHeight: 0,
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  gap: theme.spacing(1.25),
}))

const Label = styled(Typography)({
  flexGrow: 1,
}) as typeof Typography

const CheckSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  display: "flex",
  flexShrink: 0,
  visibility: selected ? "visible" : "hidden",
}))
